import { A } from '@adornis/base/env-info.js';
import { translationsMatching } from '@adornis/bh-matching-translation/translations.js';
import { languageTranslations } from '@adornis/bh-matching-user/entities/auto-matching-fields.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { Language } from '@adornis/translation-core/languages.js';
import { MatchingHautnahMenteeUser, MatchingHautnahMentorUser } from './entities/matching-user.js';

export const matchingTranslations = {
  ...translationsMatching,

  user: {
    [MatchingHautnahMenteeUser._class]: ['Schüler:in', 'Pupil'],
    [MatchingHautnahMentorUser._class]: ['Student*in', 'Student'],
    plural: {
      [MatchingHautnahMenteeUser._class]: ['Schüler:in', 'Pupils'],
      [MatchingHautnahMentorUser._class]: ['Student:innen', 'Students'],
    },
  },

  mentor: {
    singular: ['Student*in', 'Student'],
    plural: ['Student:innen', 'Students'],
    all_termins: ['Alle Student:innen', 'all students'],
  },

  mentee: {
    singular: ['Schüler:in', 'Pupil'],
    plural: ['Schüler:innen', 'Pupils'],
    all_termins: ['Alle Schüler:innen', 'all pupils'],
  },

  buddy: {
    ...translationsMatching.buddy,
    mentorSignupTitle: ['Anmeldeformular für Student:innen', 'Registration form for students'],
    menteeSignupTitle: ['Anmeldeformular für Schüler:innen', 'Registration form for pupils'],
    allMentors: ['Alle Student:innen', 'All students'],
    allMentees: ['Alle Schüler:innen', 'All Pupils'],
    exportMatchedMenteeUser: ['Alle gematchten Schüler:innen exportieren', 'Export all matched pupils Buddys'],
    exportMatchedMentorUser: ['Alle gematchten Student:innen exportieren', 'Export all matched students Buddys'],
  },

  route: {
    ...translationsMatching.route,
    'signup-mentor': ['Anmeldung Student:innen', 'Signup student'],
    'signup-mentee': ['Anmeldung Schüler:innen', 'Signup pupil'],
    'fields-mentor': ['Felder für Student:innen', 'Fields for students'],
    'fields-mentee': ['Felder für Schüler:innen', 'Fields for pupils'],
  },

  field: {
    ...translationsMatching.field,
    wohnort: ['Wohnort', 'Living Place'],
    land: ['Land', 'Country'],
    herkunftsland: ['Herkunftsland', 'Native Country'],
    staatsangehoerigkeit: ['Staatsangehörigkeit', 'Nationality'],
    geburtsjahr: ['Geburtsjahr', 'Year of Birth'],
    bachelorMaster: ['Bachelor/Master', 'Bachelor/Master'],
    studiengang: ['Studiengang', 'Course of Study'],
    geschlecht: ['Geschlecht', 'Gender'],
    geschlechtDesBuddies: ['Geschlecht des Buddies', 'Gender of the Buddy'],
    freizeitHobbies: ['Freizeit/Hobbies', 'Hobbies'],
    muttersprache: ['Muttersprache', 'Native Language'],
    fremdsprachen: ['Fremdsprachen', 'Foreign Languages'],
    wunschsprache: ['Wunschsprache', 'Desired Language'],
    anzahlMentees: ['Anzahl Schüler:innen', 'Number of Pupils'],
    numberOfMentees: ['Anzahl Schüler:innen', 'Number of Students'],
    numberOfFellowMentees: ['Schüler:innen Guppengröße', 'Group Size'],
    genderPreference: ['Geschlechtspräferenz', 'Gender Preference'],
    foreignLanguages: ['Fremdsprachen', 'Foreign Languages'],
    phoneNumber: ['Handynummer', 'Phone Number'],
    availableDates: ['Verfügbare Tage', 'Available Days'],
    studyPrograms: ['Studiengänge', 'Study Programs'],
    teachingSubjects: ['Schulfächer', 'Teaching Subjects'],
    additionalNotes: ['Anmerkungen', 'Additional Notes'],
    dataPrivacyConsent: [
      'Ich erkläre mich einverstanden mit der Datenschutzerklärung',
      'I declare my consent to the Data Privacy Consent',
    ],
    singleRegistrationConfirmed: [
      'Ich bestätige hiermit, dass ich mich im Jahr 2026 nur einmal für die Matching Days von UNI:hautnah angemeldet habe.',
      'I confirm that I am only registered once for the Matching Days of UNI:hautnah in the 2026.',
    ],
    gradeLevel: ['Klassenstufe', 'Grade Level'],
    schoolName: ['Name der Schule', 'School Name'],
    customSchoolName: ['Name der Schule (falls nicht aufgeführt)', 'School Name (if not listed)'],
    primaryStudyInterest: ['Hauptinteresse Studiengang', 'Primary Study Interest'],
    additionalStudyInterests: ['Weitere Studiengänge', 'Additional Study Interests'],
    teachingSubjectInterest: ['Lehramt Fachinteresse', 'Teaching Subject Interest'],
    sortingChooseField: ['Feld auswählen', 'Select field'],
    sortingChooseDirection: ['Reihenfolge', 'Order'],
    sortingAsc: ['Aufsteigend', 'Ascending'],
    sortingDesc: ['Absteigend', 'Descending'],
    gender: ['Geschlecht', 'Gender'],
    nativeLanguage: ['Muttersprache', 'Native Language'],
    preferredLanguage: ['Wunschsprache', 'Preferred Language'],
    note: ['Notizen', 'Notes'],
    timeframeID: ['Zeitraum', 'Timeframe'],
    matches: ['Matches', 'Matches'],
    dataPrivacyCheckbox: ['Ich willige in die Datenschutzerklärung ein', 'I consent to the data privacy policy'],
    submitButton: ['Anmelden', 'Register'],
    parentalConsentDoc: ['Einverständniserklärung', 'Parental Consent'],
  },

  field_description: {
    email: [
      'Bitte achte auf die korrekte Eingabe Deiner E-Mail-Adresse, damit sichergestellt ist, dass wir Dich bezüglich des Matchings kontaktieren können.',
      'Please make sure to enter your email address correctly so that we can contact you about the matching.',
    ],
    firstName: ['Entspricht Namen auf Teilnehmendenzertifikat', 'Matches the name on the participant certificate'],
    lastName: ['Entspricht Namen auf Teilnehmendenzertifikat', 'Matches the name on the participant certificate'],
    phoneNumber: [
      'Bei Eingabe wird sie an eure:n Matchingpartner:in weitergegeben zur schnelleren Kontaktaufnahme.',
      'When entered, it will be sent to your matching partner(s) for faster contact.',
    ],
    availableDates: [
      'Die Matching Days 2025 werden vom 11. bis 13. Mai stattﬁnden. Hier kannst Du Tage ankreuzen, an denen du Zeit hast. Achtung: Je mehr Tage Du ausschließt, desto unwahrscheinlicher wird es, dass Du gematcht wirst. Es kann außerdem sein, dass Du eventuell gar nicht gematcht werden kannst, falls Du zu viele Tage ausschließt.',
      'The matching days take place from the 11. to the 13. of Mai. You can check the days you are available. Attention: The more days you exclude, the less likely you will get matched. It can also happen that you might not get matched if you exclude too many days.',
    ],
    numberOfMentees: [
      'Wie viele Schüler:innen kannst du an deinem Matching Day mitnehmen (wie viele Schüler:innen möchtest du gleichzeitig betreuen)?',
      'How many students can you bring to your matching day (how many students do you want to care for at the same time)?',
    ],
    numberOfFellowMentees: [
      'Mit wie vielen Schülern:innen möchtest du gemeinsam betreut werden?',
      'With how many students do you want to be grouped with?',
    ],
    studyPrograms: [
      'Welche Studiengänge kannst du Schüler:innen zeigen?',
      'Which study programs can you show students?',
    ],
    teachingSubjects: [
      'Falls Du Lehramt studierst: Über welche Schulfächer kannst Du den Schüler:innen Auskunft geben?',
      'If you study teaching subjects, which subjects can you share with students?',
    ],
    foreignLanguages: ['Welche Fremdsprachen kannst du sprechen?', 'Which foreign languages can you speak?'],
    nativeLanguage: ['Was ist deine Muttersprache?', 'What is your native language?'],
  },

  enums: {
    ...translationsMatching.enums,

    // Gender enums
    MALE: ['Männlich', 'Male'],
    FEMALE: ['Weiblich', 'Female'],
    DIVERSE: ['Divers', 'Diverse'],
    NOT_SPECIFIED: ['Keine Angabe', 'Not specified'],
    SAME_SEX: ['Student:in/Schüler:in mit gleichem Geschlecht', 'Student/Pupil with same sex'],
    NO_PREFERENCE: ['Keine Präferenz', 'No preference'],

    // Language preference enums
    GERMAN: ['Deutsch', 'German'],
    GERMAN_ENGLISH: ['Deutsch und Englisch', 'German and English'],
    ENGLISH: ['Englisch', 'English'],

    // MatchingDayDate enum translations
    MONDAY_MAY_11: ['Montag, 11.05.2026', 'Monday, May 11, 2026'],
    TUESDAY_MAY_12: ['Dienstag, 12.05.2026', 'Tuesday, May 12, 2026'],
    WEDNESDAY_MAY_13: ['Mittwoch, 13.05.2026', 'Wednesday, May 13, 2026'],

    // StudyProgram enum translations
    STUDY_AFRICAN_LANGUAGES: [
      'Afrikanische Sprachen, Medien und Kommunikation',
      'African Languages, Media and Communication',
    ],
    STUDY_COMPARATIVE_LITERATURE: ['Allgemeine und vergleichende Literaturwissenschaft', 'Comparative Literature'],
    STUDY_AMERICAN_STUDIES: ['American Studies', 'American Studies'],
    STUDY_ARCHAEOLOGICAL_SCIENCES: ['Archäologische Wissenschaften', 'Archaeological Sciences'],
    STUDY_BIOCHEMISTRY: ['Biochemie', 'Biochemistry'],
    STUDY_BIOINFORMATICS: ['Bioinformatik', 'Bioinformatics'],
    STUDY_BIOPHYSICS: ['Biophysik', 'Biophysics'],
    STUDY_BIOSCIENCES: ['Biowissenschaften', 'Biosciences'],
    STUDY_CHEMISTRY: ['Chemie', 'Chemistry'],
    STUDY_EMPIRICAL_LINGUISTICS: ['Empirische Sprachwissenschaften', 'Empirical Linguistics'],
    STUDY_ENGLISH_STUDIES: ['English Studies', 'English Studies'],
    STUDY_EDUCATIONAL_SCIENCES: ['Erziehungswissenschaft', 'Educational Sciences'],
    STUDY_ETHNOLOGY: ['Ethnologie', 'Ethnology'],
    STUDY_PROTESTANT_THEOLOGY: ['Evangelische Theologie', 'Protestant Theology'],
    STUDY_GEOGRAPHY: ['Geographie', 'Geography'],
    STUDY_GEOSCIENCES: ['Geowissenschaften', 'Geosciences'],
    STUDY_GERMAN_STUDIES: ['Germanistik', 'German Studies'],
    STUDY_HISTORY: ['Geschichte', 'History'],
    STUDY_GREEK_PHILOLOGY: ['Griechische Philologie', 'Greek Philology'],
    STUDY_COMPUTER_SCIENCE: ['Informatik', 'Computer Science'],
    STUDY_KOREASTUDIES: ['Koreastudien', 'Korea Studies'],
    STUDY_ISLAMIC_STUDIES: ['Islamische Studien', 'Islamic Studies'],
    STUDY_JAPANOLOGY: ['Japanologie', 'Japanology'],
    STUDY_JUDAIC_STUDIES: ['Judaistik', 'Judaic Studies'],
    STUDY_CATHOLIC_THEOLOGY: ['Katholische Theologie', 'Catholic Theology'],
    STUDY_ART_HISTORY: ['Kunstgeschichte', 'Art History'],
    STUDY_CULTURAL_ANTHROPOLOGY: [
      'Kulturanthropologie und Europäische Ethnologie',
      'Cultural Anthropology and European Ethnology',
    ],
    STUDY_LATIN_PHILOLOGY: ['Lateinische Philologie', 'Latin Philology'],
    STUDY_SPECIAL_NEEDS_EDUCATION: ['Lehramt für Förderschulen', 'Special Needs Education'],
    STUDY_PRIMARY_SCHOOL_TEACHING: ['Lehramt für Grundschulen', 'Primary School Teaching'],
    STUDY_SECONDARY_SCHOOL_TEACHING: ['Lehramt für Gymnasien', 'Secondary School Teaching'],
    STUDY_MIDDLE_SCHOOL_TEACHING: ['Lehramt für Haupt- und Realschulen', 'Middle School Teaching'],
    STUDY_LINGUISTICS: ['Linguistik', 'Linguistics'],
    STUDY_MATHEMATICS: ['Mathematik', 'Mathematics'],
    STUDY_MEDICINE: ['Medizin', 'Medicine'],
    STUDY_MEDICAL_TECHNOLOGY: ['Medizintechnik', 'Medical Technology'],
    STUDY_METEOROLOGY: ['Meteorologie', 'Meteorology'],
    STUDY_MUSICOLOGY: ['Musikwissenschaft', 'Musicology'],
    STUDY_PHARMACY: ['Pharmazie', 'Pharmacy'],
    STUDY_PHILOSOPHY: ['Philosophie', 'Philosophy'],
    STUDY_PHYSICS: ['Physik', 'Physics'],
    STUDY_POLITICAL_SCIENCE: ['Politikwissenschaft', 'Political Science'],
    STUDY_PSYCHOLOGY: ['Psychologie', 'Psychology'],
    STUDY_LAW: ['Rechtswissenschaft', 'Law'],
    STUDY_RELIGIOUS_STUDIES: ['Religionswissenschaft', 'Religious Studies'],
    STUDY_ROMANCE_STUDIES: ['Romanistik', 'Romance Studies'],
    STUDY_SINOLOGY: ['Sinologie', 'Sinology'],
    STUDY_SCANDINAVIAN_STUDIES: ['Skandinavistik', 'Scandinavian Studies'],
    STUDY_SOCIOLOGY: ['Soziologie', 'Sociology'],
    STUDY_SPORTS_SCIENCE: ['Sportwissenschaft', 'Sports Science'],
    STUDY_SOUTHEAST_ASIAN_STUDIES: ['Sprachen und Kulturen Südostasiens', 'Languages and Cultures of Southeast Asia'],
    STUDY_THEATRE_FILM_MEDIA: ['Theater-, Film- u. Medienwissenschaft', 'Theatre, Film and Media Studies'],
    STUDY_ECONOMICS_EDUCATION: ['Wirtschaftspädagogik', 'Economics Education'],
    STUDY_ECONOMICS: ['Wirtschaftswissenschaften', 'Economics'],
    STUDY_DENTISTRY: ['Zahnmedizin', 'Dentistry'],

    // schools enum translations
    Adolf_Reichwein_Schule: ['Adolf-Reichwein-Schule', 'Neu-Anspach'],
    Adorno_Gymnasium: ['Adorno Gymnasium', 'Frankfurt'],
    Albert_Einstein_Gymnasium: ['Albert-Einstein-Gymnasium', 'Maintal'],
    Albert_Einstein_Schule: ['Albert-Einstein-Schule', 'Schwalbach'],
    Albert_Schweitzer_Schule: ['Albert-Schweitzer-Schule', 'Offenbach'],
    Altkonigschule: ['Altkönigschule', 'Kronberg'],
    Anna_Schmidt_Schule: ['Anna-Schmidt-Schule', 'Frankfurt'],
    Begemann_Schule: ['Begemann-Schule', 'Frankfurt'],
    Bertha_von_Suttner_Schule: ['Bertha-von-Suttner-Schule', 'Mörfelden-Walldorf'],
    Berufliche_Schulen_Gross_Gerau: ['Berufliche Schulen Groß-Gerau', 'Groß-Gerau'],
    Berufliches_Gymnasium_des_Main_Kinzig_Kreises: ['Berufliches Gymnasium des Main-Kinzig-Kreises', 'Gelnhausen'],
    Bischof_Neumann_Schule: ['Bischof-Neumann-Schule', 'Königstein'],
    Carl_von_Weinberg_Schule: ['Carl-von-Weinberg-Schule', 'Frankfurt'],
    Claus_von_Stauffenberg_Schule: ['Claus-von-Stauffenberg-Schule', 'Rodgau'],
    Dreieichschule: ['Dreieichschule', 'Langen'],
    Eduard_Stiehler_Schule: ['Eduard-Stiehler-Schule', 'Fulda'],
    Eichendorffschule: ['Eichendorffschule', 'Kelkheim'],
    Einhardschule: ['Einhardschule', 'Seligenstadt'],
    Elisabethenschule: ['Elisabethenschule', 'Frankfurt'],
    Ernst_Gobel_Schule: ['Ernst-Göbel-Schule', 'Höchst im Odenwald'],
    Ernst_Reuter_Schule: ['Ernst-Reuter-Schule', 'Frankfurt'],
    Fanny_Leicht_Gymnasium: ['Fanny-Leicht-Gymnasium', 'Vaihingen'],
    Feldbergschule: ['Feldbergschule', 'Oberursel'],
    Ferdinand_Braun_Schule: ['Ferdinand-Braun-Schule', 'Fulda'],
    Franziskanergymnasium: ['Franziskanergymnasium', 'Kreuzburg'],
    Freiherr_vom_Stein_Schule_Frankfurt: ['Freiherr-vom-Stein-Schule', 'Frankfurt'],
    Freiherr_vom_Stein_Schule_Fulda: ['Freiherr-vom-Stein-Schule', 'Fulda'],
    Friedrich_Dessauer_Gymnasium_Frankfurt: ['Friedrich-Dessauer-Gymnasium', 'Frankfurt'],
    Friedrich_Dessauer_Gymnasium_Aschaffenburg: ['Friedrich-Dessauer-Gymnasium', 'Aschaffenburg'],
    Georg_Ackermann_Schule: ['Georg-Ackermann-Schule', 'Breuberg'],
    Georg_Buchner_Gymnasium: ['Georg-Büchner-Gymnasium', 'Bad Vilbel'],
    Gesamtschule_Gedern: ['Gesamtschule Gedern', 'Gedern'],
    Gesamtschule_Konradsdorf: ['Gesamtschule Konradsdorf', 'Konradsdorf'],
    Goethe_Gymnasium: ['Goethe-Gymnasium', 'Frankfurt'],
    Goetheschule: ['Goetheschule', 'Neu-Isenburg'],
    Graf_Stauffenberg_Gymnasium: ['Graf-Stauffenberg-Gymnasium', 'Flörsheim'],
    Grimmelshausen_Gymnasium: ['Grimmelshausen-Gymnasium', 'Gelnhausen'],
    Gymnasium_Oberursel: ['Gymnasium Oberursel', 'Oberursel'],
    Gymnasium_Riedberg: ['Gymnasium Riedberg', 'Frankfurt'],
    Hanns_Seidel_Gymnasium: ['Hanns-Seidel-Gymnasium', 'Hösbach'],
    Heinrich_Boll_Schule: ['Heinrich-Böll-Schule', 'Hattersheim'],
    Heinrich_Mann_Schule: ['Heinrich-Mann-Schule', 'Dietzenbach'],
    Heinrich_von_Gagern_Gymnasium: ['Heinrich-von-Gagern-Gymnasium', 'Frankfurt'],
    Heinrich_von_Kleist_Schule: ['Heinrich-von-Kleist-Schule', 'Eschborn'],
    Helmholtzschule: ['Helmholtzschule', 'Frankfurt'],
    Henry_Harnischfeger_Schule: ['Henry-Harnischfeger-Schule', 'Bad Soden-Salmünster'],
    Hermann_Staudinger_Gymnasium: ['Hermann-Staudinger-Gymnasium', 'Erlenbach am Main'],
    Hochtaunusschule: ['Hochtaunusschule', 'Oberursel'],
    Hohe_Landesschule_Hanau: ['Hohe Landesschule Hanau', 'Hanau'],
    Humboldtschule: ['Humboldtschule', 'Bad Homburg'],
    Immanuel_Kant_Schule: ['Immanuel-Kant-Schule', 'Rüsselsheim'],
    IGS_Stierstadt: ['IGS Stierstadt', 'Stierstadt'],
    Internatsschule_Schloss_Hansenberg: ['Internatsschule Schloss Hansenberg', 'Geisenheim-Johannisberg'],
    Johannes_Butzbach_Gymnasium: ['Johannes-Butzbach-Gymnasium', 'Miltenberg'],
    Julius_Echter_Gymnasium: ['Julius-Echter-Gymnasium', 'Elsenfeld'],
    Kaiserin_Friedrich_Gymnasium: ['Kaiserin-Friedrich-Gymnasium', 'Bad Homburg'],
    Karl_Rehbein_Schule: ['Karl-Rehbein-Schule', 'Hanau'],
    Karl_Theodor_von_Dalberg_Gymnasium: ['Karl-Theodor-von-Dalberg-Gymnasium', 'Aschaffenburg'],
    Kinzig_Schule: ['Kinzig-Schule', 'Schlüchtern'],
    Konrad_Zuse_Schule: ['Konrad-Zuse-Schule', 'Hünfeld'],
    Kopernikusschule: ['Kopernikusschule', 'Freigericht'],
    Kronberg_Gymnasium: ['Kronberg-Gymnasium', 'Aschaffenburg'],
    Landgraf_Ludwig_Gymnasium: ['Landgraf-Ludwig-Gymnasium', 'Gießen'],
    Lessing_Gymnasium: ['Lessing-Gymnasium', 'Frankfurt'],
    Liebigschule_Frankfurt: ['Liebigschule (Frankfurt)', 'Liebigschule (Frankfurt)'],
    Liebigschule_Giessen: ['Liebigschule (Gießen)', 'Liebigschule (Gießen)'],
    Limesschule: ['Limesschule', 'Altenstadt'],
    Main_Taunus_Schule: ['Main-Taunus-Schule', 'Hofheim'],
    Maria_Ward_Schule_Aschaffenburg: ['Maria-Ward-Schule', 'Aschaffenburg'],
    Maria_Ward_Schule_BadHomburg: ['Maria-Ward-Schule', 'Bad Homburg'],
    Marianum: ['Marianum', 'Fulda'],
    Marienschule_Offenbach: ['Marienschule', 'Offenbach'],
    Marienschule_Fulda: ['Marienschule', 'Fulda'],
    Max_Beckmann_Schule: ['Max-Beckmann-Schule', 'Frankfurt'],
    Max_Eyth_Schule: ['Max-Eyth-Schule', 'Dreieich'],
    Max_Planck_Schule: ['Max-Planck-Schule', 'Rüsselsheim'],
    Musterschule: ['Musterschule', 'Frankfurt'],
    Neues_Gymnasium: ['Neues Gymnasium', 'Rüsselsheim'],
    Otto_Hahn_Schule_Frankfurt: ['Otto-Hahn-Schule', 'Frankfurt'],
    Otto_Hahn_Schule_Hanau: ['Otto-Hahn-Schule', 'Hanau'],
    Paul_Gerhardt_Schule: ['Paul-Gerhardt-Schule', 'Hanau'],
    Philipp_Reis_Schule: ['Philipp-Reis-Schule', 'Friedrichsdorf'],
    Pralat_Diehl_Schule: ['Prälat-Diehl-Schule', 'Groß-Gerau'],
    Rabanus_Maurus_Schule: ['Rabanus-Maurus-Schule', 'Fulda'],
    Ricarda_Huch_Schule: ['Ricarda-Huch-Schule', 'Gießen'],
    Richard_Muller_Schule: ['Richard-Müller-Schule', 'Fulda'],
    Robert_Koch_Schule_Frankfurt: ['Robert-Koch-Schule', 'Frankfurt'],
    Rudolf_Koch_Schule: ['Rudolf-Koch-Schule', 'Offenbach'],
    St_Angela_Schule: ['St. Angela-Schule', 'Königstein'],
    Schillerschule: ['Schillerschule', 'Frankfurt'],
    Schule_am_Ried: ['Schule-am-Ried', 'Frankfurt'],
    Schule_und_Internat_Steinmuhle: ['Schule und Internat Steinmühle', 'Marburg'],
    Singbergschule: ['Singbergschule', 'Wölfersheim'],
    Strothoff_International_School: ['Strothoff International School', 'Dreieich'],
    Taunusgymnasium: ['Taunusgymnasium', 'Königstein'],
    Ulrich_von_Hutten_Gymnasium: ['Ulrich-von-Hutten-Gymnasium', 'Schlüchtern'],
    Waldorfschule: ['Waldorfschule', 'Frankfurt'],
    Weibelfeldschule: ['Weibelfeldschule', 'Dreieich'],
    Wigbertschule: ['Wigbertschule', 'Hünfeld'],
    Winfriedschule: ['Winfriedschule', 'Fulda'],
    Wohlerschule: ['Wöhlerschule', 'Frankfurt'],
    Wolfgang_Ernst_Gymnasium: ['Wolfgang-Ernst-Gymnasium', 'Büdingen'],
    Ziehenschule: ['Ziehenschule', 'Frankfurt'],

    // GradeLevel enum translations
    Q4: ['Q4', 'Q4'],
    Q2: ['Q2', 'Q2'],
    E2: ['E2', 'E2'],
    GRADE_10_OR_LOWER: ['10. Klasse oder niedriger', 'Grade 10 or lower'],
    GRADUATED: ['Schule bereits abgeschlossen', 'Already graduated'],

    // TeachingSubject enum translations
    SUBJECT_BIOLOGY: ['Biologie', 'Biology'],
    SUBJECT_CHEMISTRY: ['Chemie', 'Chemistry'],
    SUBJECT_GERMAN: ['Deutsch', 'German'],
    SUBJECT_ENGLISH: ['Englisch', 'English'],
    SUBJECT_GEOGRAPHY: ['Erdkunde / Geographie', 'Geography'],
    SUBJECT_PROTESTANT_RELIGION: ['Evangelische Religion', 'Protestant Religion'],
    SUBJECT_FRENCH: ['Französisch', 'French'],
    SUBJECT_HISTORY: ['Geschichte', 'History'],
    SUBJECT_GREEK: ['Griechisch', 'Greek'],
    SUBJECT_COMPUTER_SCIENCE: ['Informatik', 'Computer Science'],
    SUBJECT_ISLAMIC_RELIGION: ['Islamische Religion', 'Islamic Religion'],
    SUBJECT_ITALIAN: ['Italienisch', 'Italian'],
    SUBJECT_CATHOLIC_RELIGION: ['Katholische Religion', 'Catholic Religion'],
    SUBJECT_ART: ['Kunst', 'Art'],
    SUBJECT_LATIN: ['Latein', 'Latin'],
    SUBJECT_MATHEMATICS: ['Mathematik', 'Mathematics'],
    SUBJECT_MUSIC: ['Musik', 'Music'],
    SUBJECT_PHILOSOPHY: ['Philosophie', 'Philosophy'],
    SUBJECT_PHYSICS: ['Physik', 'Physics'],
    SUBJECT_POLITICS_ECONOMICS: ['Politik & Wirtschaft', 'Politics & Economics'],
    SUBJECT_SPANISH: ['Spanisch', 'Spanish'],
    SUBJECT_SPORTS: ['Sport', 'Sports'],

    ...languageTranslations,
  },

  timeframe: {
    ...translationsMatching.timeframe,

    expiredUserDataBanner: {
      title: ['Veraltete Semesterdaten gefunden!', 'Timeframe data is expired'],
      instruction: [
        'Jetzt alle vergangenen Nutzer-Semester-Daten löschen!',
        'Delete all past user-timeframe-data now!',
      ],
    },

    expiredUserDataInfo: {
      title: ['Älter als 1 Jahr!', 'Older than 1 year!'],
      description1: ['Jetzt alle vergangenen', 'Delete all past semester'],
      description2: ['Semester-Daten löschen!', 'data now!'],
    },
  },
};

export type TranslationDictionaryMatching = typeof matchingTranslations;
if (!A.isServer) {
  clientTranslator.updateDictionary(matchingTranslations, [Language.GERMAN, Language.ENGLISH]);

  if (A.isDevelopment) globalThis.clientTranslator = clientTranslator;
}
